import { FC, LegacyRef, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { PickupDeliveryLocationResponseV1, PickupDeliveryLocationStatus } from '@forto-dev/customer-master-data-models'
import { Select } from '@forto-dev/phoenix-ui-core'

import { pickupDeliveryLocationsQueries } from '@/features/locations/api/pickupDeliveryLocation.queries'

import { LocationSearchFooter } from './LocationSearchFooter'
import { LocationSearchOption } from './LocationSearchOption'
import { useLocationSelectorContext } from '../context/useLocationSelectorContext'
import { useLocationSelectorDispatchContext } from '../context/useLocationSelectorDispatchContext'

const statusLabelMap = {
  [PickupDeliveryLocationStatus.ACTIVE]: 'Active addresses',
  [PickupDeliveryLocationStatus.INACTIVE]: 'Inactive addresses',
}

export const LocationSearch: FC = () => {
  const { accountID, label, value, disabled, required, inputRef, errorMessage, selectProps } =
    useLocationSelectorContext()
  const { onSelect: onChange, onBlur: _ } = useLocationSelectorDispatchContext()

  const { data: { items } = { items: [], total: 0 } } = useQuery({
    ...pickupDeliveryLocationsQueries.list({
      accountID,
      orderBy: 'locationName',
      limit: 1000,
    }),
  })

  const options = useMemo(
    () =>
      items
        .sort((optionItemA, optionItemB) => optionItemA.status.localeCompare(optionItemB.status))
        .map((optionItem) => ({
          label: `${optionItem.locationName}${optionItem.addressFormatted ? `, ${optionItem.addressFormatted}` : ''}`,
          value: optionItem.id,
          group: statusLabelMap[optionItem.status],
          disabled: optionItem.status === PickupDeliveryLocationStatus.INACTIVE,
        })),
    [items]
  )

  const optionIDMap = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc[item.id] = item
        return acc
      },
      {} as Record<string, PickupDeliveryLocationResponseV1>
    )
  }, [items])

  return (
    <Select
      {...selectProps}
      items={options}
      value={value}
      label={label}
      disabled={disabled}
      required={required}
      ref={inputRef as LegacyRef<HTMLInputElement> | undefined}
      state={!errorMessage ? undefined : 'negative'}
      hint={errorMessage || selectProps?.hint}
      onChange={(selectedValue) => {
        onChange?.(optionIDMap[selectedValue] ?? null)
      }}
      searchable={true}
      renderCustomItem={(selectedItem) => (
        <LocationSearchOption option={optionIDMap[selectedItem.value]} selected={value === selectedItem.value} />
      )}
      autocomplete={false}
      customAction={<LocationSearchFooter />}
    />
  )
}
